import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { useGetCountries } from "api/queries/common/useGetCountries";
import { useGetCurrencies } from "api/queries/common/useGetCurrencies";
import { useGetPackingListConfig } from "api/queries/location/useGetPackingListConfig";
import { useGetPaymentLocationFormDetails } from "api/queries/location/useGetPaymentLocationFormDetails";
import { capitalizeFirstLetter } from "helpers/commonHelpers";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { getPaymentLocationDetailsParams } from "@RequestFormV2/utils/getPaymentLocationDetailsParams";
import {
  unitMeasurementCmOrInch,
  unitMeasurementKgOrLbs,
} from "pages/shipper/RequestForm/components/PackingList/PackingListDefaultOptions";
import { getDangerousGoodsOptionsV2 } from "@RequestFormV2/sections/PackingList/utils/dangerousGoodsOptions";
import { getTemperatureOptionsV2 } from "@RequestFormV2/sections/PackingList/utils/temperatureOptions";
import { CONTAINER_HANDLING_UNIT } from "@RequestFormV2/defaults/packingListDefaults";
import { buildHandlingUnitHashMap } from "@RequestFormV2/sections/PackingList/utils/buildHandlingUnitHashMap";

export function usePackingListData() {
  const intl = useIntl();
  const mode = useRequestFormMode();
  const draftId = useRequestFormDraftId();
  const location = useWatch({ name: "organizational.location" });

  const { data: packingListConfig, isLoading: isLoadingPackingListConfig } = useGetPackingListConfig(location ?? "", {
    enabled: !!location,
  });

  // Shares the React Query cache with the Organisation section (same params) to source
  // the location's default currency for commodity rows.
  const { data: locationDetails } = useGetPaymentLocationFormDetails({
    location: location ?? "",
    config: {
      params: getPaymentLocationDetailsParams(mode, location ?? "", draftId).params,
    },
    options: { enabled: !!location },
    isQuote: mode === "quote",
  });

  const defaultCurrency = locationDetails?.default_currency ?? "";

  const { data: currencies = [], isLoading: isLoadingCurrencies } = useGetCurrencies();
  // Same query key as the Schedule section, so this reuses its cached country list.
  const { data: countries = [] } = useGetCountries({ useV2: true, refetchOnMount: false });

  const handlingUnitHashMap = useMemo(
    () => buildHandlingUnitHashMap(packingListConfig?.handling_unit_options),
    [packingListConfig?.handling_unit_options],
  );

  const handlingUnitOptions = useMemo(() => {
    const options = packingListConfig?.handling_unit_options ?? [];
    return options
      .filter((item) => Object.keys(item)[0] !== CONTAINER_HANDLING_UNIT)
      .map((item) => {
        const key = Object.keys(item)[0];
        const settings = item[key];
        return {
          label: capitalizeFirstLetter(settings.label),
          value: key,
        };
      });
  }, [packingListConfig?.handling_unit_options]);

  const containerTypeOptions = useMemo(() => {
    const containerEntry = packingListConfig?.handling_unit_options?.find((item) => "container" in item);
    const container = containerEntry?.container as { handling_unit_options?: { key: string; value: string }[] } | undefined;
    return (
      container?.handling_unit_options?.map((option) => ({
        label: option.value,
        value: option.key,
      })) ?? []
    );
  }, [packingListConfig?.handling_unit_options]);

  const nmfcOptions = useMemo(
    () =>
      packingListConfig?.nmfc_values?.map(([value, desc]) => ({
        label: value,
        value,
        supportingText: desc,
      })) ?? [],
    [packingListConfig?.nmfc_values],
  );

  const currencyOptions = useMemo(
    () =>
      currencies.map((currency) => ({
        label: `(${currency.code}) ${currency.name ?? currency.code}`,
        value: currency.code,
      })),
    [currencies],
  );

  // Commodity origin country is stored as the ISO alpha-3 code (the API's `value`).
  const countryOptions = useMemo(
    () => countries.map((country) => ({ label: country.label, value: country.value })),
    [countries],
  );

  const dimensionUnitOptions = useMemo(
    () => unitMeasurementCmOrInch(intl).map((opt) => ({ label: opt.label, value: opt.value })),
    [intl],
  );

  const weightUnitOptions = useMemo(
    () => unitMeasurementKgOrLbs(intl).map((opt) => ({ label: opt.label, value: opt.value })),
    [intl],
  );

  const temperatureOptions = useMemo(() => getTemperatureOptionsV2(intl), [intl]);

  const dangerousGoodsSelectOptions = useMemo(() => getDangerousGoodsOptionsV2(intl), [intl]);

  return useMemo(
    () => ({
      location,
      packingListConfig,
      isLoadingPackingListConfig,
      handlingUnitHashMap,
      handlingUnitOptions,
      containerTypeOptions,
      nmfcOptions,
      currencyOptions,
      countryOptions,
      defaultCurrency,
      dimensionUnitOptions,
      weightUnitOptions,
      temperatureOptions,
      dangerousGoodsSelectOptions,
      isLoadingCurrencies,
      bulkUploadEnabled: Boolean(packingListConfig?.bulk_upload_enabled),
      dimensionDefault: packingListConfig?.dimension_uom_default ?? "cm",
      weightDefault: packingListConfig?.weight_uom_default ?? "kg",
    }),
    [
      location,
      packingListConfig,
      isLoadingPackingListConfig,
      handlingUnitHashMap,
      handlingUnitOptions,
      containerTypeOptions,
      nmfcOptions,
      currencyOptions,
      countryOptions,
      defaultCurrency,
      dimensionUnitOptions,
      weightUnitOptions,
      temperatureOptions,
      dangerousGoodsSelectOptions,
      isLoadingCurrencies,
    ],
  );
}
